import { useEffect, useState, type ReactNode } from 'react';
import StockCard from '../StockCard';
import { fetchSignalsForStocks } from '../../hooks/strategyExecutionHelpers';
import type { CrossMarketStatus } from '../../services/crossMarketApi';
import type { AIStockData } from '../../types/watchlist';
import { getCurrentWatchlist } from '../../services/watchlistApi';

type Watchlist = CrossMarketStatus['markets']['US']['live_watchlist'];

export default function TossLiveWatchlistPanel({ watchlist }: { watchlist?: Watchlist }) {
  const [stocks, setStocks] = useState<AIStockData[]>([]);
  const [loading, setLoading] = useState(false);
  const tickerKey = watchlist?.tickers.join(',') || '';

  useEffect(() => {
    let cancelled = false;
    async function loadSignals() {
      const tickers = tickerKey ? tickerKey.split(',') : [];
      if (!tickers.length) {
        setStocks([]);
        return;
      }
      const recommendations = await loadRecommendationMap();
      const base = toStocks(tickers, recommendations);
      setStocks(base.map((stock) => ({ ...stock, signal_status: 'loading' })));
      setLoading(true);
      const withSignals = await fetchSignalsForStocks(base, 'live');
      if (!cancelled) {
        setStocks(withSignals);
        setLoading(false);
      }
    }
    loadSignals();
    return () => { cancelled = true; };
  }, [tickerKey]);

  if (!watchlist) {
    return <PanelShell><div className="empty">US live watchlist 데이터 없음</div></PanelShell>;
  }

  return (
    <PanelShell watchlist={watchlist} loading={loading}>
      <div className="rate-limit-warning">
        <div className="warning-header">LIVE pilot Toss 검증 universe</div>
        <div className="warning-content">
          <p>• 이 목록은 자동 매수 목록이 아니라 Toss 주문 가능성을 검증한 universe입니다.</p>
          <p>• 실제 주문은 이 안에서 live 전략 신호와 리스크 게이트가 BUY일 때만 실행됩니다.</p>
          <p>• 후보 갱신은 System Overview의 Gemini→Toss 검증 경로로만 반영됩니다.</p>
          <p>• BUY가 없으면 후보/신호/리스크 단계 중 어디서 막혔는지 분리 확인합니다.</p>
          <p>• 아래 신호는 live profile 기준 read-only 표시입니다.</p>
          <p>• 첫 주문 확인 전까지 화면 수동 실행은 제공하지 않습니다.</p>
        </div>
      </div>
      <div className="ai-stocks-grid">
        {stocks.map((stock) => (
          <StockCard key={stock.ticker} stock={stock} />
        ))}
      </div>
    </PanelShell>
  );
}

function PanelShell({
  children,
  watchlist,
  loading = false,
}: {
  children: ReactNode;
  watchlist?: Watchlist;
  loading?: boolean;
}) {
  return (
    <div className="ai-watchlist-panel">
      <div className="panel-header">
        <h3>US Live Universe (Toss 검증)</h3>
        <div className="panel-actions">
          <div className="countdown">
            source: {watchlist?.source || '없음'} · {loading ? '신호 조회 중' : watchlist?.ready ? '검증 완료' : '미검증'}
          </div>
        </div>
      </div>
      {children}
    </div>
  );
}

async function loadRecommendationMap(): Promise<Map<string, AIStockData>> {
  try {
    const data = await getCurrentWatchlist();
    const recommendations = data.full_data?.recommendations || [];
    return new Map(recommendations.map((item) => [item.ticker, item]));
  } catch {
    return new Map();
  }
}

function toStocks(tickers: string[], recommendations: Map<string, AIStockData>): AIStockData[] {
  return tickers.map((ticker) => ({
    ...liveStockFallback(ticker),
    ...recommendations.get(ticker),
    ticker,
    catalyst_label: recommendations.has(ticker) ? 'Gemini 후보 사유' : 'Universe 편입 사유',
    confidence_label: recommendations.has(ticker) ? 'Gemini 신뢰도' : undefined,
    risk_label: recommendations.has(ticker) ? 'Gemini 리스크' : undefined,
    screening_metadata_valid: recommendations.has(ticker),
    source_hint: recommendations.get(ticker)?.source_hint || 'toss_us_validated',
  }));
}

function liveStockFallback(ticker: string): AIStockData {
  return {
    ticker,
    sector: 'Toss US',
    confidence: 0,
    risk_level: 0,
    catalyst: 'Toss 검증을 통과한 US live_pilot universe',
    source_hint: 'toss_us_validated',
    screening_metadata_valid: false,
  };
}
